import React from "react";
import type { ApolloClient } from "@apollo/client";
import StepWizard from "components/StepWizard";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, find } from "lodash";
import { closeAndLiquidateAccountWithDisbursement } from "actions/userActions";
import { push } from "store/actions/router";
import { toast } from "react-toastify";
import {
  setAccountForClose,
  setAccountPendingClosure,
} from "actions/accountActions";

import IconSpinner from "components/IconSpinner";
import AddressConfirmation from "../distributions/AddressConfirmation";
import DistributionFederalTax from "../distributions/DistributionFederalTax";
import DistributionStateTax from "../distributions/DistributionStateTax";
import DistributionSummary from "../distributions/DistributionSummary";
import DistributionReason from "../distributions/DistributionReason";
import DistributionSupport from "../distributions/DistributionSupport";
import DistributionType from "../distributions/DistributionType";
import { getDistributionTypeFromAccountType } from "statics/accountTypes";
import type { RootState } from "types/state";
import type {
  DistributionAmountSelection,
  FederalTaxSelection,
  StateTaxSelection,
} from "../distributions/distributionTypes";

interface StepWizardApi {
  goToNamedStep: (name: string) => void;
  goToStep: (n: number) => void;
  nextStep: () => void;
  previousStep: () => void;
}

const mapStateToProps = (state: RootState) => ({
  userInfo: state.user.profile,
  accounts: state.accounts.iraAccounts,
  selectedAccountId: state.accounts.closeAccount?.accountId,
  accountType: state.accounts.closeAccount?.accountType,
  client: state.user.client,
});

const mapDispatchToProps = {
  closeAndLiquidateAccountWithDisbursement,
  push,
  setAccountForClose,
  setAccountPendingClosure,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// redux's mapStateToProps also surfaces `client` (state.user.client), but
// withApollo injects `client` optionally; drop the redux copy from the type so
// the HOC's optional `client?` doesn't clash with a required redux `client`.
// At runtime connect still overrides with the redux client, preserving behavior.
type Props = WithApolloClient<Omit<PropsFromRedux, "client">>;

interface State {
  isLoading: boolean;
  selectedAccountType?: string;
  distributionAmountSelection: DistributionAmountSelection;
  // initialized from getDistributionTypeFromAccountType ("ROTH" | "TRADITIONAL")
  // but DistributionType may later set an arbitrary string (or undefined), so
  // the field is widened to match _handleDistributionTypeSelect.
  distributionType: string | undefined;
  distributionSpecialReason: string;
  federalTaxSelection: FederalTaxSelection;
  stateTaxSelection: StateTaxSelection;
  isAccountClosure: boolean;
  activeStep?: number;
}

export class CloseAccountWorkflow extends React.PureComponent<Props, State> {
  wizard!: StepWizardApi;

  constructor(props: Props) {
    super(props);

    const selectedAccount = find(props.accounts, {
      id: props.selectedAccountId,
    });

    this.state = {
      isLoading: true,
      selectedAccountType: props.accountType,
      distributionAmountSelection: {
        distributionAmount: "ENTIRE_ACCOUNT_BALANCE",
        amount: get(selectedAccount, "balances.totalAccountBalance", 0),
        isTotalDisbursement: true,
      },
      // accountType was a required prop in the original (PropTypes .isRequired);
      // closeAccount may be null in state, so cast to preserve the unconditional call.
      distributionType: getDistributionTypeFromAccountType(
        props.accountType as string
      ),
      distributionSpecialReason: "",
      federalTaxSelection: {
        federalTaxWithholdingPercentage: "",
        percentage: "",
      },
      stateTaxSelection: {
        stateTaxWithholding: "",
        stateTaxWithholdingPercentage: "",
        stateTaxWithholdingAmount: "",
      },
      isAccountClosure: true,
    };
  }

  async componentDidMount() {
    const selectedAccount = find(this.props.accounts, {
      id: this.props.selectedAccountId,
    });
    const totalAccountBalance = get(
      selectedAccount,
      "balances.totalAccountBalance",
      0
    );

    this.setState({
      isLoading: false,
      distributionAmountSelection: {
        distributionAmount: "ENTIRE_ACCOUNT_BALANCE",
        amount: totalAccountBalance,
        isTotalDisbursement: true,
      },
    });
  }

  _onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  _handleDistributionReasonSelection = (distributionSpecialReason: string) => {
    this.setState({ distributionSpecialReason });
  };

  handleFederalTaxSelection = (federalTaxSelection: FederalTaxSelection) => {
    this.setState({ federalTaxSelection });
  };

  _handleStateTaxSelection = (stateTaxSelection: StateTaxSelection) => {
    this.setState({ stateTaxSelection });
  };

  // DistributionType's onDistributionTypeSelect emits `string | undefined`;
  // widen the param to match its callback signature and pass straight through
  // to state (the original JS set whatever the child supplied).
  _handleDistributionTypeSelect = (distributionType: string | undefined) => {
    this.setState({ distributionType });
  };

  handleComplete = async () => {
    const { selectedAccountId, client } = this.props;

    console.log("Account closure request details:", {
      client,
      selectedAccountId,
      fullState: this.state,
    });

    if (!selectedAccountId) {
      console.error("No account ID provided for closure");
      toast.error("Missing account information");
      return;
    }

    try {
      const input = {
        accountId: selectedAccountId,
        distributionType: this.state.distributionType,
        distributionSpecialReason: this.state.distributionSpecialReason,
        federalTaxWithholdingPercentage:
          parseFloat(this.state.federalTaxSelection.percentage) || 0,
        stateTaxWithholdingAmount: this.state.stateTaxSelection
          .stateTaxWithholdingAmount
          ? parseFloat(this.state.stateTaxSelection.stateTaxWithholdingAmount)
          : 0,
        stateTaxWithholdingPercentage:
          parseFloat(
            this.state.stateTaxSelection.stateTaxWithholdingPercentage
          ) || 0,
        distributionAmount: parseFloat(
          // amount is stored as a number; the original passes it straight to
          // parseFloat (which coerces it) — cast preserves that behavior.
          this.state.distributionAmountSelection.amount as unknown as string
        ),
        isTotalDisbursement: true,
      };

      // The dispatched lifecycle resolves to a SUCCESS ({type,data}) or
      // FAILURE ({type,error:string}) action; narrow to read the loose shape
      // the original JS relied on (.error is only present on the failure branch).
      // CloseAccountWithDisbursementInput requires strict enum literals for
      // distributionType/distributionSpecialReason; the original JS passed
      // through whatever strings the flow supplied, so cast at the boundary to
      // preserve that behavior.
      const result = (await this.props.closeAndLiquidateAccountWithDisbursement(
        client as ApolloClient<object>,
        input as Parameters<
          typeof this.props.closeAndLiquidateAccountWithDisbursement
        >[1]
      )) as { error?: string } | undefined;

      console.log("Account closure result:", result);

      if (!result) {
        throw new Error("No response received from account closure request");
      }

      if (result.error) {
        throw new Error(result.error);
      }

      this.props.setAccountPendingClosure(
        selectedAccountId,
        this.props.accountType as string
      );
      toast.success("Successfully submitted account closure request.");
      this.props.push("/dashboard/profile/close-account");
    } catch (error) {
      console.error("Account closure error:", error);
      toast.error("There was an error closing the account. Please try again.");
    }
  };

  _setInstance = (wizardInstance: StepWizardApi) => {
    this.wizard = wizardInstance;
  };

  render() {
    if (this.state.isLoading) {
      return <IconSpinner centered />;
    }

    const { selectedAccountId, accountType } = this.props;
    if (!selectedAccountId || !accountType) {
      this.props.push("/dashboard/profile");
      return null;
    }

    const isRothDistribution = accountType === "ROTH_IRA";
    const totalAccountBalance = get(
      find(this.props.accounts, { id: selectedAccountId }),
      "balances.totalAccountBalance"
    );

    return (
      <div className="mega-container" id="close-account-workflow">
        <div className="main-content">
          <StepWizard
            instance={this._setInstance}
            showNavigation={false}
            onStepChange={this._onStepChange}
            isLazyMount={true}
            transitions={{
              enterRight: "",
              enterLeft: "",
              exitRight: "",
              exitLeft: "",
            }}
            props={{ isAccountClosure: this.state.isAccountClosure }}
          >
            <AddressConfirmation
              stepName="addressConfirmation"
              nextStepName="distributionType"
              dob={this.props.userInfo.dob}
              accounts={this.props.accounts}
              isRothDistribution={isRothDistribution}
              selectedAccountType={this.state.selectedAccountType}
              goToNamedStep={(step) => this.wizard.goToNamedStep(step)}
              isAccountClosure={this.state.isAccountClosure}
            />
            <DistributionType
              stepName="distributionType"
              selectedAccountType={this.state.selectedAccountType}
              distributionType={this.state.distributionType}
              onDistributionTypeSelect={this._handleDistributionTypeSelect}
              onDistributionReasonSelect={
                this._handleDistributionReasonSelection
              }
              goToNamedStep={() =>
                this.wizard.goToNamedStep("distributionSupport")
              }
              isAccountClosure={this.state.isAccountClosure}
            />
            <DistributionSupport
              stepName="distributionSupport"
              selectedAccountType={this.props.selectedAccountId}
              distributionType={this.state.distributionType}
              distributionAmount={this.state.distributionAmountSelection}
              isAccountClosure={this.state.isAccountClosure}
            />
            <DistributionReason
              stepName="distributionReason"
              onDistributionReasonSelect={
                this._handleDistributionReasonSelection
              }
            />
            <DistributionFederalTax
              stepName="distributionFederalTax"
              // profile carries an index signature (unknown fields); cast to
              // string at the boundary (same convention as DistributionStateTax).
              userState={this.props.userInfo.state as string}
              isRothDistribution={isRothDistribution}
              federalTaxSelection={this.state.federalTaxSelection}
              onFederalTaxSelection={this.handleFederalTaxSelection}
            />
            <DistributionStateTax
              stepName="distributionStateTax"
              userState={this.props.userInfo.state as string}
              isRothDistribution={isRothDistribution}
              // totalAccountBalance is Maybe<number>|undefined off the lodash
              // get; the original passed it straight through. Cast at boundary.
              accountValue={totalAccountBalance as number | undefined}
              // amount is number|string|null|undefined; the original passed it
              // straight to withdrawalAmount (typed number). Cast at boundary.
              withdrawalAmount={
                this.state.distributionAmountSelection.amount as number
              }
              federalTaxSelection={this.state.federalTaxSelection}
              isTotalDisbursement={true}
              onStateTaxSelection={this._handleStateTaxSelection}
              isAccountClosure={this.state.isAccountClosure}
            />
            <DistributionSummary
              stepName="distributionSummary"
              selectedAccountType={selectedAccountId}
              distributionType={this.state.distributionType}
              distributionSpecialReason={this.state.distributionSpecialReason}
              distributionAmountSelection={
                this.state.distributionAmountSelection
              }
              federalTaxSelection={this.state.federalTaxSelection}
              stateTaxSelection={this.state.stateTaxSelection}
              isRothDistribution={isRothDistribution}
              onComplete={this.handleComplete}
              showConfirmationModal={true}
              isAccountClosure={this.state.isAccountClosure}
            />
          </StepWizard>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(CloseAccountWorkflow));
